
import { createContext, ReactNode } from "react";
import { ApiRequest, ApiTrace, EnvironmentType, FusionSummary, PreviewSummaryReport } from "src/@type/api/api";
import { ApiRequestDTO, ApiTraceDTO, FusionSummaryDTO, PreviewSummaryReportDTO } from "./DTO/DTO";
import { mapperApiRequestDTOToModel, mapperApiTraceDTOToModel, mapperFusionSummaryDTOToModel, mapperPreviewSummaryReportDTOToModel } from "./DTO/DTOToModel";
import { getInvariantDateString } from "src/utils/formatTime";
import useAuth from "src/hooks/useAuth";

type ApiContextType = {
    getApiTraces: () => Promise<ApiTrace[]>
    getApiRequests: (apiRequestId: string) => Promise<ApiRequest[]>
    getFusionSummary: (date: Date, environment: EnvironmentType) => Promise<FusionSummary>
    getPreviewSummaryReport: (date: Date, environment: EnvironmentType) => Promise<PreviewSummaryReport>
    sendPreviewSummaryReport: (date: Date, environment: EnvironmentType) => Promise<void>
};

export const ApiDataContext = createContext<ApiContextType | null>(null);

type ApiDataContextTypeProviderProps = {
    children: ReactNode;
};

export function ApiDataContextProvider({ children }: ApiDataContextTypeProviderProps) {
    const { axios } = useAuth();

    const getApiTraces = () => new Promise<ApiTrace[]>((resolve, reject) => {
        axios.get('/mid/api/api-traces', {

        })
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve((data as ApiTraceDTO[]).map(mapperApiTraceDTOToModel));
            });
    });

    const getApiRequests = (apiRequestId: string) => new Promise<ApiRequest[]>((resolve, reject) => {
        axios.get('/mid/api/api-trace', {
            params: {
                apiRequestId
            }
        })
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve((data as ApiRequestDTO[]).map(mapperApiRequestDTOToModel));
            });
    });



    const getFusionSummary = (date_in: Date, environment: 'DEV' | 'PRO') => new Promise<FusionSummary>((resolve, reject) => {
        axios.get('/mid/api/fusion-summary/', {
            params: {
                date_in: getInvariantDateString(date_in),
                environment: `${environment}%`
            }
        })
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve(mapperFusionSummaryDTOToModel(data as FusionSummaryDTO));
            });
    });

    const getPreviewSummaryReport = (date: Date, environment: EnvironmentType) => new Promise<PreviewSummaryReport>((resolve, reject) => {
        axios.get('/mid/api/fusion-summary/previewSummaryReport', {
            params: {
                date: getInvariantDateString(date),
                environment: environment
            }
        })
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve(mapperPreviewSummaryReportDTOToModel(data as PreviewSummaryReportDTO));
            });
    });

    const sendPreviewSummaryReport = (date: Date, environment: EnvironmentType) => new Promise<void>((resolve, reject) => {
        axios.post('/mid/api/fusion-summary/sendPreviewSummaryReport', {
            date: getInvariantDateString(date),
            environment: environment
        }
        )
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve();
            });
    });

    return (
        <ApiDataContext.Provider
            value={{
                getApiTraces,
                getApiRequests,
                getFusionSummary,
                getPreviewSummaryReport,
                sendPreviewSummaryReport
            }}
        >
            {children}
        </ApiDataContext.Provider>
    );
}